import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/api";
import { environment } from "../../environments/environment";
import { DashboardTitle } from "../../components/DashboardTitle";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useSelector } from "react-redux";
import { IAppState } from "../../store/store";
// import { currencyFormat, showCurrency } from "../../utility/currencyView";

// Response data type definitions
interface ProductImage {
  uuid: string;
  media_url: string;
  media_title: string;
}

interface Product {
  uuid: string;
  product_title: string;
  product_description: string;
  yourself_price: number;

  friends_price: number;

  currency: string;
  location: string;
  category: string;
  minimum_quantity: number;
}

interface FriendsOrderResponse {
  data: {
    images: ProductImage[];
    order_deadline: string;
    product: Product;
    profile_picture: string;
    user_name: string;
  };
  message: string;
  success: boolean;
}

export const InvitedFriendsCheckout = () => {
  const location = useLocation();
  const isLoggedIn = useSelector((state: IAppState) => state.auth.token);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<FriendsOrderResponse | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [orderUuid, setOrderUuid] = useState("");
  const [productUuid, setProductUuid] = useState("");
  const navigate = useNavigate()
  
  const currencyView = "$"

  useEffect(() => {
    const fetchFriendsOrderData = async () => {
      try {
        // Get query parameters from URL
        const searchParams = new URLSearchParams(location.search);
        const urlOrderUuid = searchParams.get("o");
        const urlProductUuid = searchParams.get("p");
        const urlQuantity = searchParams.get("q");
        const urlFirstName = searchParams.get("f");
        const urlLastName = searchParams.get("l");
        
        // Set the name and quantity from URL parameters
        if (urlFirstName) setFirstName(urlFirstName);
        if (urlLastName) setLastName(urlLastName);
        if (urlQuantity) setQuantity(parseInt(urlQuantity, 10) || 1);
        if (urlProductUuid) setProductUuid(urlProductUuid);
        
        // Validate required parameters - use the URL parameter directly
        if (!urlOrderUuid) {
          setError("Missing order_uuid parameter");
          setIsLoading(false);
          return;
        }
        
        // Set orderUuid after validation
        setOrderUuid(urlOrderUuid);

        // Make API call with the order UUID from URL params
        const response = await api.get(
          `${environment.baseUrl}/product/friends-order?uuid=${urlOrderUuid}`
        );

        // Store the response
        setResponseData(response.data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching friends order data:", error);
        setError("Failed to fetch data");
        setIsLoading(false);
      }
    };

    fetchFriendsOrderData();
  }, [location.search]);

  // Countdown timer effect
  useEffect(() => {
    if (!responseData?.data?.order_deadline) return;

    const calculateTimeLeft = () => {
      const difference = new Date(responseData.data.order_deadline).getTime() - new Date().getTime();
      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      };
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, [responseData]);

  // Image carousel controls
  const nextImage = () => {
    if (responseData?.data?.images?.length) {
      setActiveImageIndex((prev) => (prev + 1) % responseData.data.images.length);
    }
  };

  const prevImage = () => {
    if (responseData?.data?.images?.length) {
      setActiveImageIndex((prev) =>
        prev === 0 ? responseData.data.images.length - 1 : prev - 1
      );
    }
  };

  // Quantity adjustment handlers
  const incrementQuantity = () => {
    setQuantity(prev => prev + 1);
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  // Proceed to checkout handler
  const handleCheckout = () => {
    if (!isLoggedIn) {
      sessionStorage.setItem("order_uuid", orderUuid);
      sessionStorage.setItem("friend_product_uuid", productUuid);
      sessionStorage.setItem("friend_product_qty", quantity.toString());
      navigate("/login");
      return;
    } else {
      navigate(`/process-friends-order?o=${orderUuid}&p=${productUuid}&q=${quantity}`);
    }
  };

  // Countdown Timer Component
  const CountdownTimer = () => (
    timeLeft && (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100 shadow-sm">
        <h3 className="text-lg md:text-xl font-semibold text-center text-blue-800 mb-3">
          Time Left to Purchase
        </h3>
        <div className="flex justify-center gap-4">
          <div className="text-center bg-white px-3 py-2 rounded-lg shadow-sm min-w-[60px]">
            <p className="text-2xl md:text-3xl font-bold text-blue-700">{timeLeft.days}</p>
            <p className="text-xs text-gray-500 uppercase font-medium">Days</p>
          </div>
          <div className="text-center bg-white px-3 py-2 rounded-lg shadow-sm min-w-[60px]">
            <p className="text-2xl md:text-3xl font-bold text-blue-700">{timeLeft.hours}</p>
            <p className="text-xs text-gray-500 uppercase font-medium">Hours</p>
          </div>
          <div className="text-center bg-white px-3 py-2 rounded-lg shadow-sm min-w-[60px]">
            <p className="text-2xl md:text-3xl font-bold text-blue-700">{timeLeft.minutes}</p>
            <p className="text-xs text-gray-500 uppercase font-medium">Min</p>
          </div>
          <div className="text-center bg-white px-3 py-2 rounded-lg shadow-sm min-w-[60px]">
            <p className="text-2xl md:text-3xl font-bold text-blue-700">{timeLeft.seconds}</p>
            <p className="text-xs text-gray-500 uppercase font-medium">Sec</p>
          </div>
        </div>
        <p className="text-center text-sm text-blue-600 mt-4">
          Group purchase closing soon! Complete your purchase to join.
        </p>
      </div>
    )
  );

  return (
    <section className="web_padding_x web_padding_y !pt-5">
      <DashboardTitle text="Group Purchase Invitation"></DashboardTitle>

      {/* Invitation Banner */}
      {firstName && lastName && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 md:p-6 rounded-xl border border-indigo-100 shadow-sm mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-center text-indigo-900">
            <span className="capitalize">{firstName} {lastName}</span> has invited you to join this group purchase!
          </h2>
          <p className="text-center text-indigo-700 mt-2">
            Join now to unlock discounted pricing and complete the group order.
          </p>
        </div>
      )}

      <div className="mt-6">
        {isLoading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        ) : responseData?.data ? (
          <div>
            {/* Mobile view - Countdown Timer at the top */}
            <div className="md:hidden mb-6">
              <CountdownTimer />
            </div>

            {/* Desktop 2-column layout */}
            <div className="flex flex-col md:flex-row md:gap-6">
              {/* Left Column - Product Details */}
              <div className="w-full md:w-2/3 bg-white rounded-xl shadow-md overflow-hidden">
                {/* Product Carousel */}
                <div className="relative h-64 sm:h-80 bg-gray-100">
                  {responseData.data.images && responseData.data.images.length > 0 ? (
                    <>
                      <img
                        src={responseData.data.images[activeImageIndex].media_url}
                        alt={responseData.data.images[activeImageIndex].media_title || "Product image"}
                        className="w-full h-full object-cover"
                      />
                      {/* Carousel controls with React Icons */}
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md"
                        aria-label="Previous image"
                      >
                        <FiChevronLeft className="h-6 w-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md"
                        aria-label="Next image"
                      >
                        <FiChevronRight className="h-6 w-6" />
                      </button>
                      {/* Dot indicators */}
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                        {responseData.data.images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setActiveImageIndex(index)}
                            className={`h-2 w-2 rounded-full ${
                              index === activeImageIndex ? "bg-blue-600" : "bg-gray-300"
                            }`}
                            aria-label={`Go to image ${index + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No images available</p>
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">
                        {responseData.data.product.product_title}
                      </h2>
                      <p className="text-gray-600 mt-1">{responseData.data.product.location}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Category</p>
                      <p className="font-medium">{responseData.data.product.category}</p>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-6">{responseData.data.product.product_description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Regular Price</p>
                      <p className="text-xl font-bold line-through text-gray-500">
                        {currencyView}{" "}
                        {responseData.data.product.yourself_price}
                      </p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Group Price</p>
                      <p className="text-xl font-bold text-green-600">
                        {currencyView}{" "}
                        {responseData.data.product.friends_price}
                      </p>
                    </div>
                  </div>

                  {/* Quantity Selection */}
                  <div className="mb-6">
                    <p className="text-gray-700 font-medium mb-2">Quantity</p>
                    <div className="inline-block bg-gray-100 px-4 py-2 rounded-lg font-medium text-gray-800">
                      {quantity}
                    </div>
                  </div>

                  {/* Total Price */}
                  <div className="mb-6 bg-blue-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <p className="font-medium text-blue-800">Total Price:</p>
                      <p className="text-xl font-bold text-blue-800">
                        {currencyView}{" "}
                       {responseData.data.product.friends_price * quantity}
                      </p>
                    </div>
                  </div>

                  <button 
                    onClick={handleCheckout} 
                    className="w-full bg-[#0e203f] text-white py-3 px-4 rounded-lg mt-2 font-medium hover:bg-blue-700 transition"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </div>

              {/* Right Column - Time Remaining (desktop only) */}
              <div className="hidden md:block md:w-1/3">
                <CountdownTimer />
                
                <div className="bg-green-50 border border-green-100 rounded-xl p-5 mt-6">
                  <h3 className="font-semibold text-green-800 mb-2">Group Purchase Benefits</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="inline-block w-4 h-4 bg-green-200 rounded-full mr-2 mt-1"></span>
                      <span className="text-sm text-green-700">Lower price than buying individually</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-4 h-4 bg-green-200 rounded-full mr-2 mt-1"></span>
                      <span className="text-sm text-green-700">Help complete the minimum order quantity</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-4 h-4 bg-green-200 rounded-full mr-2 mt-1"></span>
                      <span className="text-sm text-green-700">Join the group and share shipping costs</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-5 mt-6">
                  <h3 className="font-semibold text-yellow-800 mb-2">Minimum Order Requirements</h3>
                  <p className="text-sm text-yellow-700">
                    This group purchase requires a minimum of {responseData.data.product.minimum_quantity} items 
                    to qualify for the discounted price. Join the group now to help reach this goal!
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
            No product data available
          </div>
        )}
      </div>
    </section>
  );
};